/**
 * Shared hook: loads product nicknames from amazon_local_products once,
 * provides a lookup function sku → display name.
 *
 * Usage:
 *   const { displayName } = useNicknames();
 *   <td>{displayName(row.sku)}</td>
 */
import { useEffect, useState } from 'react';
import { api } from './api.js';

interface Product {
  sku: string;
  nickname: string | null;
  title: string | null;
  parent_nickname: string | null;
}

interface NicknameData {
  display: Map<string, string>;
  nick: Map<string, string>;
  parent: Map<string, string>;
}

let cachedData: NicknameData | null = null;
let loading = false;
const listeners: Array<() => void> = [];

async function loadNicknames(): Promise<NicknameData> {
  if (cachedData) return cachedData;
  if (loading) {
    return new Promise(resolve => {
      listeners.push(() => resolve(cachedData!));
    });
  }
  loading = true;
  try {
    const res = await api.get<{ products: Product[] }>('/inventory/local-products');
    const display = new Map<string, string>();
    const nick = new Map<string, string>();
    const parent = new Map<string, string>();
    for (const p of res.products) {
      if (p.nickname) { display.set(p.sku, p.nickname); nick.set(p.sku, p.nickname); }
      else if (p.title) display.set(p.sku, p.title.length > 40 ? p.title.slice(0, 40) + '...' : p.title);
      if (p.parent_nickname) parent.set(p.sku, p.parent_nickname);
    }
    cachedData = { display, nick, parent };
    for (const cb of listeners) cb();
    listeners.length = 0;
    return cachedData;
  } catch {
    cachedData = { display: new Map(), nick: new Map(), parent: new Map() };
    return cachedData;
  } finally {
    loading = false;
  }
}

export function useNicknames() {
  const [data, setData] = useState<NicknameData>(cachedData ?? { display: new Map(), nick: new Map(), parent: new Map() });

  useEffect(() => {
    loadNicknames().then(setData);
  }, []);

  return {
    /** Returns nickname or title if available, otherwise the raw SKU */
    displayName: (sku: string) => data.display.get(sku) ?? sku,
    /** Returns actual nickname only, not title fallback (or null) */
    nickname: (sku: string) => data.nick.get(sku) ?? null,
    /** Returns parent product nickname (or null) */
    parentNickname: (sku: string) => data.parent.get(sku) ?? null,
    /** The raw display map */
    map: data.display,
  };
}
